import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { MeResponse } from '../../entities/me/me-contract.types';
import { getMe, isAuthContextRequired, postStaffLogin, type StaffLoginPayload } from '../../entities/me/me.api';
import { ApiError } from '../api/api-error';
import { createApiClient } from '../api/api-client';
import { resolveStaffBootstrapState, type MeHttpBlocker } from '../bootstrap/bootstrap-state';
import { userFacingActionError, userFacingLoadError } from '../lib/staff-user-facing-error';
import { AuthContext, type StaffAuthMeRefreshOptions } from './auth-context';
import {
  clearAuthSession,
  getStoredAccessToken,
  getStoredImpersonateOrgId,
  getStoredRefreshToken,
  getStoredSuperadminNoOrg,
  setStoredAccessToken,
  setStoredImpersonateOrgId,
  setStoredSuperadminNoOrg,
  setStoredRefreshToken
} from './auth-session';

export function AuthProvider({ children }: { children: ReactNode }) {
  const tokenRef = useRef<string | null>(getStoredAccessToken());
  const impersonateRef = useRef<number | null>(getStoredImpersonateOrgId());
  /** СА: после «Без контекста» все GET /auth/me с `?organization_id=0`, пока не выбрана имперсонация в орг. */
  const superadminMeOrgClearRef = useRef(getStoredSuperadminNoOrg());

  const [tokenVersion, setTokenVersion] = useState(0);
  const [me, setMe] = useState<MeResponse | null>(null);
  const meRef = useRef<MeResponse | null>(null);
  meRef.current = me;
  const [impersonateOrgId, setImpersonateOrgIdState] = useState<number | null>(impersonateRef.current);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [meHttpBlocker, setMeHttpBlocker] = useState<MeHttpBlocker | null>(null);

  const logout = useCallback(() => {
    clearAuthSession();
    tokenRef.current = null;
    impersonateRef.current = null;
    superadminMeOrgClearRef.current = false;
    setImpersonateOrgIdState(null);
    setMe(null);
    setFetchError(null);
    setMeHttpBlocker(null);
    setTokenVersion((x) => x + 1);
  }, []);

  /** Только для цепочки refresh в `createApiClient`: токены в ref/storage + bump версии (без await `/auth/me`). */
  const commitRefreshTokensSync = useCallback((accessToken: string, refreshToken: string | null) => {
    tokenRef.current = accessToken;
    setStoredAccessToken(accessToken);
    setStoredRefreshToken(refreshToken);
    setTokenVersion((x) => x + 1);
  }, []);

  const client = useMemo(
    () =>
      createApiClient({
        getAccessToken: () => tokenRef.current,
        getImpersonateOrganizationId: () => impersonateRef.current,
        getRefreshToken: getStoredRefreshToken,
        applySessionTokens: commitRefreshTokensSync,
        clearSessionAfterFailedRefresh: logout
      }),
    [commitRefreshTokensSync, logout]
  );

  const hydrateSessionAfterNewTokens = useCallback(
    async (accessToken: string, refreshToken: string | null) => {
      impersonateRef.current = null;
      setStoredImpersonateOrgId(null);
      setImpersonateOrgIdState(null);
      superadminMeOrgClearRef.current = true;
      setStoredSuperadminNoOrg(true);
      setFetchError(null);
      setMeHttpBlocker(null);
      const m = await getMe(client, { accessTokenOverride: accessToken, superadminClearEffectiveOrg: true });
      if ((m.role ?? '').trim().toLowerCase() !== 'superadmin') {
        superadminMeOrgClearRef.current = false;
        setStoredSuperadminNoOrg(false);
      }
      tokenRef.current = accessToken;
      setStoredAccessToken(accessToken);
      setStoredRefreshToken(refreshToken);
      setMe(m);
      setFetchError(null);
      setMeHttpBlocker(null);
      setTokenVersion((x) => x + 1);
    },
    [client]
  );

  const loadMe = useCallback(async (opts?: StaffAuthMeRefreshOptions): Promise<MeResponse | null> => {
    if (!tokenRef.current) {
      setMe(null);
      setFetchError(null);
      setMeHttpBlocker(null);
      return null;
    }
    setFetchError(null);
    setMeHttpBlocker(null);
    try {
      if (opts?.superadminClearEffectiveOrg) {
        superadminMeOrgClearRef.current = true;
        setStoredSuperadminNoOrg(true);
      }
      const m = await getMe(client, {
        superadminClearEffectiveOrg: superadminMeOrgClearRef.current
      });
      setMeHttpBlocker(null);
      setMe(m);
      return m;
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 401) {
          logout();
          return null;
        }
        if (e.status === 403 && e.errorType === 'module_unavailable') {
          setMeHttpBlocker({
            kind: 'module_unavailable',
            moduleCode: e.moduleCode,
            message: userFacingActionError(e)
          });
          setMe(null);
          return null;
        }
        if (e.status === 403) {
          setMeHttpBlocker({ kind: 'forbidden', message: userFacingActionError(e) });
          setMe(null);
          return null;
        }
      }
      setFetchError(userFacingLoadError(e));
      if (!meRef.current) {
        setMe(null);
      }
      return null;
    }
  }, [client, logout]);

  useEffect(() => {
    void loadMe();
  }, [loadMe, tokenVersion, impersonateOrgId]);

  const login = useCallback(
    async (payload: StaffLoginPayload) => {
      setFetchError(null);
      setMeHttpBlocker(null);
      const res = await postStaffLogin(client, payload);
      if (isAuthContextRequired(res)) {
        return res;
      }
      if (!res.access_token) throw new Error('Не получен access_token');
      await hydrateSessionAfterNewTokens(res.access_token, res.refresh_token ?? null);
      return res;
    },
    [client, hydrateSessionAfterNewTokens]
  );

  const applySessionTokens = hydrateSessionAfterNewTokens;

  const refreshMe = useCallback(async (opts?: StaffAuthMeRefreshOptions) => await loadMe(opts), [loadMe]);

  const setImpersonateOrgId = useCallback(async (organizationId: number | null) => {
    if (organizationId == null || organizationId <= 0) {
      superadminMeOrgClearRef.current = true;
      setStoredSuperadminNoOrg(true);
    }
    impersonateRef.current = organizationId;
    setStoredImpersonateOrgId(organizationId);
    setImpersonateOrgIdState(organizationId);
    if (organizationId != null && organizationId > 0) {
      superadminMeOrgClearRef.current = false;
      setStoredSuperadminNoOrg(false);
    }
  }, []);

  const tokenPresent = Boolean(tokenRef.current);

  const bootstrapState = useMemo(
    () =>
      resolveStaffBootstrapState({
        tokenPresent,
        me,
        impersonateOrganizationId: impersonateOrgId,
        fetchError,
        meHttpBlocker
      }),
    [tokenPresent, me, impersonateOrgId, fetchError, meHttpBlocker]
  );

  const value = useMemo(
    () => ({
      me,
      impersonateOrgId,
      fetchError,
      bootstrapState,
      apiClient: client,
      login,
      applySessionTokens,
      logout,
      refreshMe,
      setImpersonateOrgId
    }),
    [me, impersonateOrgId, fetchError, bootstrapState, client, login, applySessionTokens, logout, refreshMe, setImpersonateOrgId]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
